import { useState, useEffect } from "react";
import {
  Autocomplete,
  CurrencyInput,
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
} from "../../../components";
import { AddLayout } from "../../../layouts";
import {
  GrupoServicoService,
  notification,
  ServicoService,
} from "../../../services";
import { InputErros, PermisoesHelper } from "../../../helpers";
import { userLogs, responseErros, unformatPrice, voltar } from "../../../utils";
import servicoValidator from "./middlewares/servico.validator";
import { Servico } from "./entities/servico.entity";
import { useImportContext } from "../../../contexts/import.context";

const servicoService = new ServicoService();
const grupoServicoService = new GrupoServicoService();

let servicoEntity = new Servico({
  nome: null,
  descricao: null,
  grupoServicoId: null,
  precoServico: null,
});

const AddServico = ({ match }) => {
  const permissoesHelper = new PermisoesHelper();
  permissoesHelper.validarPermisao("servicos-visualizar");
  const { id } = match.params;
  const [loading, setLoading] = useState(false);
  const { initialCollapses } = useImportContext();
  const [servico, setServico] = useState(servicoEntity);
  const [inputErro, setInputErro] = useState([]);
  const [userLog, setUserLog] = useState({});
  const [grupoServico, setGrupoServico] = useState(null);
  const [grupoServicoList, setGrupoServicoList] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);

  useEffect(() => {
    buscaServico();
  }, []);

  const buscaServico = async () => {
    if (!id) {
      await buscarGrupoServico();
      return;
    }
    setLoading(true);
    const result = await servicoService.getById(id);
    if (!result.isAxiosError) {
      servicoEntity = new Servico(result.data);
      setServico({ ...servicoEntity });
      await buscarGrupoServicoById(result.data?.grupoServicoId);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscarGrupoServico = async (filtros) => {
    const filtrosTemp = {
      ...filtros,
      ativado: true,
      nonPaginated: true,
    };
    grupoServicoService.getAll(filtrosTemp).then((result) => {
      if (!result.isAxiosError) {
        setGrupoServicoList(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarGrupoServicoById = async (grupoServicoId) => {
    const result = await grupoServicoService.getById(grupoServicoId);
    if (!result.isAxiosError) {
      setGrupoServico(result.data);
    } else {
      responseErros(result);
    }
  };

  async function onClickLog() {
    if ("userCreatedLog" in userLog) return;
    const logs = await userLogs(servico);
    setUserLog(logs);
  }

  const handleInputChange = (event) => {
    event.persist();
    servicoEntity.setHandleChangeInput(event.target.name, event.target.value);
    setServico({ ...servicoEntity });
  };

  const handleInputBlur = (event) => {
    event.persist();
    servicoEntity.setHandleChangeInput(
      event.target.name,
      unformatPrice(event.target.value),
    );
    setServico({ ...servicoEntity });
  };

  const handleChangeSwitch = (e) => {
    servicoEntity.setHandleChangeSwitch(e.target.name, e.target.checked);
    setServico({ ...servicoEntity });
  };

  const handleChangeAutocomplete = (name, value) => {
    servicoEntity.setHandleChangeAutoComplete(name, value);
    if (name === "grupoServicoId") {
      setGrupoServico(value);
    }
    setServico((prevState) => ({ ...prevState, ...servicoEntity }));
  };

  const handleSubmit = async (indiceSelecionado) => {
    setLoading(true);
    servicoValidator
      .validate(servicoEntity, { abortEarly: false })
      .then(async () => {
        if (id) {
          const response = await servicoService.atualizar(id, servicoEntity);
          setLoading(false);
          if (!response.isAxiosError) {
            notification.alteracaoSucesso();
            initialCollapses();
            handleCancelar();
            voltar();
          } else {
            responseErros(response);
          }
        } else {
          const response = await servicoService.cadastrar(servicoEntity);
          setLoading(false);
          if (!response.isAxiosError) {
            notification.cadastroSucesso();
            if (indiceSelecionado === 0) {
              initialCollapses();
              handleCancelar();
              voltar();
            } else {
              handleCancelar();
            }
          } else {
            responseErros(response);
          }
        }
      })
      .catch((err) => {
        setLoading(false);
        inputErros.set(err);
      });
  };

  const handleCancelar = () => {
    servicoEntity = new Servico({
      nome: null,
      descricao: null,
      grupoServicoId: null,
      precoServico: null,
    });
    setServico({ ...servicoEntity });
  };

  return (
    <AddLayout
      id={id}
      title={id ? "Editar Serviço" : "Cadastrar Serviço"}
      onClickSalvar={handleSubmit}
      loading={loading}
      userLog={id ? userLog : null}
      onClickLog={onClickLog}
      showUpdate={permissoesHelper.temPermisao("servicos-editar")}
      actions={
        <>
          <FormControlLabel
            control={
              <Switch
                checked={servico?.ativado}
                onChange={handleChangeSwitch}
                name="ativado"
                color="primary"
              />
            }
            label={servico?.ativado ? "Ativado" : "Desativado"}
          />
        </>
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <TextField
            id="nome"
            name="nome"
            label="Nome"
            variant="outlined"
            margin="normal"
            value={servico?.nome || ""}
            onChange={(e) => {
              setInputErro([]);
              handleInputChange(e);
            }}
            fullWidth
            required
            error={inputErros.get("nome")}
          />
        </Grid>
        <Grid item xs={8}>
          <TextField
            id="descricao"
            name="descricao"
            label="Descrição"
            variant="outlined"
            margin="normal"
            value={servico?.descricao || ""}
            onChange={(e) => {
              setInputErro([]);
              handleInputChange(e);
            }}
            fullWidth
            required
            error={inputErros.get("descricao")}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <CurrencyInput
            id="precoServico"
            name="precoServico"
            label="Preço do Serviço"
            variant="outlined"
            margin="normal"
            fullWidth
            value={servico?.precoServico || ""}
            onBlur={handleInputBlur}
            error={inputErros.get("precoServico")}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              required
              id="grupoServicoId"
              name="grupoServicoId"
              options={grupoServicoList}
              autoHighlight
              getOptionLabel={(option) =>
                option.descricao ? `${option.descricao}` : ""
              }
              getOptionSelected={(option, value) =>
                option?.descricao === value?.descricao
              }
              noOptionsText="Sem opções"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Grupo de Serviço"
                  variant="outlined"
                  error={inputErros.get("grupoServicoId")}
                />
              )}
              onChange={(_, newValue) => {
                handleChangeAutocomplete("grupoServicoId", newValue);
              }}
              value={grupoServico}
            />
          </FormControl>
        </Grid>
      </Grid>
    </AddLayout>
  );
};

export default AddServico;
